import { useMemo, useState } from "react"
import { ColumnDef } from "@tanstack/react-table"
import {
  CheckCircleIcon,
  ClockIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline"
import { DataTable } from "@/components/DataTable/DataTable"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { Layout } from "../components/Layout"
import { useDetectJobs, type DetectJob } from "@/hooks/useDetectJobs"
import { useExportJobs, type ExportJob } from "@/hooks/useExportJobs"

type JobStatusFilter = "all" | "running"
type JobKind = "detect" | "export"
type JobKindFilter = "all" | JobKind

// A row is a detect/export job tagged with its origin so the merged table can
// render a Kind badge and filter by it.
type Job = (DetectJob | ExportJob) & { kind: JobKind }

const activeStatuses = new Set([
  "queued",
  "downloading",
  "extracting",
  "classifying",
  "exporting",
  "encoding",
  "uploading",
  "running",
])
const completeStatuses = new Set(["complete", "completed", "done", "success"])
const errorStatuses = new Set(["error", "failed", "failure"])

function statusKind(job: Job): "active" | "complete" | "error" | "other" {
  const status = job.status.toLowerCase()
  if (job.error || errorStatuses.has(status)) return "error"
  if (activeStatuses.has(status)) return "active"
  if (completeStatuses.has(status)) return "complete"
  return "other"
}

function StatusBadge({ job }: { job: Job }) {
  const kind = statusKind(job)
  const styles = {
    active: "bg-blue-100 text-blue-800",
    complete: "bg-green-100 text-green-800",
    error: "bg-red-100 text-red-800",
    other: "bg-gray-100 text-gray-700",
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[kind]}`}>
      {job.status.replace("_", " ")}
    </span>
  )
}

function KindBadge({ kind }: { kind: JobKind }) {
  const styles: Record<JobKind, string> = {
    detect: "bg-indigo-100 text-indigo-800",
    export: "bg-amber-100 text-amber-800",
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[kind]}`}>
      {kind === "detect" ? "Detect" : "Export"}
    </span>
  )
}

function formatAge(value: string): string {
  const timestamp = new Date(value).getTime()
  if (Number.isNaN(timestamp)) return "—"

  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000))
  if (seconds < 10) return "now"
  if (seconds < 60) return `${seconds}s ago`

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function formatUpdatedAt(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "Invalid date"
  return date.toLocaleString()
}

function StatCard({ label, value, icon }: { label: string; value: number | string; icon: React.ReactNode }) {
  return (
    <div className="bg-surface rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 text-secondary text-sm mb-1">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-semibold text-primary">{value}</div>
    </div>
  )
}

function ToggleFilter<T extends string>({
  options,
  value,
  onChange,
  labelFor,
}: {
  options: readonly T[]
  value: T
  onChange: (value: T) => void
  labelFor: (value: T) => string
}) {
  return (
    <div className="inline-flex rounded-md border border-border bg-background p-1">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={`h-8 min-w-[84px] rounded px-3 text-sm font-medium transition-colors ${
            value === option
              ? "bg-primary text-contrast"
              : "text-secondary hover:text-primary"
          }`}
        >
          {labelFor(option)}
        </button>
      ))}
    </div>
  )
}

export function Jobs() {
  const [status, setStatus] = useState<JobStatusFilter>("all")
  const [kindFilter, setKindFilter] = useState<JobKindFilter>("all")

  // Only fetch the kind(s) we need. Disabling the unused query avoids polling an
  // endpoint whose rows we'd immediately filter out.
  const detectActive = kindFilter !== "export"
  const exportActive = kindFilter !== "detect"
  const detect = useDetectJobs({ status, enabled: detectActive })
  const exportJobs = useExportJobs({ status, enabled: exportActive })

  // Only consider a query's loading/error/freshness state when its kind is the
  // active facet. react-query keeps the last error of a *disabled* query in
  // state, so without this gate a one-sided upstream failure in "All" mode would
  // keep the page in ErrorFallback after switching to the other kind, hiding
  // valid rows until a full reload.
  const isLoading =
    (detectActive && detect.isLoading && detect.fetchStatus !== "idle") ||
    (exportActive && exportJobs.isLoading && exportJobs.fetchStatus !== "idle")
  const error = (detectActive ? detect.error : null) ?? (exportActive ? exportJobs.error : null)
  const dataUpdatedAt = Math.max(
    detectActive ? detect.dataUpdatedAt : 0,
    exportActive ? exportJobs.dataUpdatedAt : 0
  )

  const jobs = useMemo<Job[]>(() => {
    const detectRows: Job[] =
      kindFilter === "export" ? [] : (detect.data ?? []).map((job) => ({ ...job, kind: "detect" as const }))
    const exportRows: Job[] =
      kindFilter === "detect" ? [] : (exportJobs.data ?? []).map((job) => ({ ...job, kind: "export" as const }))

    return [...detectRows, ...exportRows].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    )
  }, [detect.data, exportJobs.data, kindFilter])

  const stats = useMemo(() => {
    return jobs.reduce(
      (acc, job) => {
        const kind = statusKind(job)
        acc.total += 1
        if (kind === "active") acc.active += 1
        if (kind === "complete") acc.complete += 1
        if (kind === "error") acc.error += 1
        return acc
      },
      { total: 0, active: 0, complete: 0, error: 0 }
    )
  }, [jobs])

  const columns = useMemo<ColumnDef<Job>[]>(
    () => [
      {
        accessorKey: "kind",
        header: "Kind",
        cell: ({ row }) => <KindBadge kind={row.original.kind} />,
      },
      {
        accessorKey: "job_id",
        header: "Job",
        cell: ({ row }) => (
          <span className="font-mono text-sm text-primary">{row.original.job_id}</span>
        ),
      },
      {
        accessorKey: "email",
        header: "User",
        cell: ({ row }) => (
          <span className="text-sm text-primary">{row.original.email || "—"}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge job={row.original} />,
      },
      {
        accessorKey: "progress",
        header: "Progress",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm text-primary">
            {row.original.progress || "—"}
          </span>
        ),
      },
      {
        accessorKey: "updated_at",
        header: "Age",
        cell: ({ row }) => (
          <div>
            <div className="text-sm text-primary">{formatAge(row.original.updated_at)}</div>
            <div className="text-xs text-secondary">{formatUpdatedAt(row.original.updated_at)}</div>
          </div>
        ),
      },
      {
        accessorKey: "error",
        header: "Error",
        cell: ({ row }) => (
          <span
            className={`block max-w-xs truncate text-sm ${
              row.original.error ? "text-red-400" : "text-secondary"
            }`}
            title={row.original.error || undefined}
          >
            {row.original.error || "—"}
          </span>
        ),
      },
    ],
    []
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Jobs</h1>
            <p className="text-secondary">Detect and export job status from the video chopper</p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <div className="flex flex-wrap gap-2">
              <ToggleFilter
                options={["all", "detect", "export"] as const}
                value={kindFilter}
                onChange={setKindFilter}
                labelFor={(value) =>
                  value === "all" ? "All" : value === "detect" ? "Detect" : "Export"
                }
              />
              <ToggleFilter
                options={["all", "running"] as const}
                value={status}
                onChange={setStatus}
                labelFor={(value) => (value === "all" ? "All" : "Running")}
              />
            </div>
            <div className="text-xs text-secondary">
              Updated {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : "—"}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            label="Active"
            value={stats.active}
            icon={<ClockIcon className="h-4 w-4" />}
          />
          <StatCard
            label="Complete"
            value={stats.complete}
            icon={<CheckCircleIcon className="h-4 w-4" />}
          />
          <StatCard
            label="Errors"
            value={stats.error}
            icon={<XCircleIcon className="h-4 w-4" />}
          />
          <StatCard
            label="Total"
            value={stats.total}
            icon={<CpuChipIcon className="h-4 w-4" />}
          />
        </div>

        {stats.error > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-red-900/50 bg-red-950/30 p-4">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-300" />
            <span className="text-sm text-red-100">
              {stats.error} {stats.error === 1 ? "job is" : "jobs are"} reporting errors.
            </span>
          </div>
        )}

        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : jobs.length === 0 ? (
          <EmptyState
            icon={<CpuChipIcon className="h-12 w-12" />}
            title="No jobs"
            description="No running or retained jobs were returned."
          />
        ) : (
          <DataTable columns={columns} data={jobs} searchKey="email" />
        )}
      </div>
    </Layout>
  )
}
